import { lastValueFrom, Observable } from 'rxjs';
import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment as config } from '../../../../environments/environment';
import { Params } from '@angular/router';
import { ChangeOrderRequest, DataResponse, KeyValuePair, StatusRequest } from '../../shared/models/models';
import {
  ExperienceTargets,
  ExperienceRemarketing,
  ExperienceUnit,
  ExperienceUnitGroup,
  ExperienceUnitGroupListItem,
  ExperienceUnitImage,
  ExperienceUnitListItem,
  SurveyQuestionForAudience,
  UnitLead,
  UnitVisibilityType
} from '../experience/models';
import { map } from 'rxjs/operators';
import * as _ from 'lodash-es';
import { InsightsUnitGraphItem, InsightsUnitStats, SurveyQuestionResult, UnitAllTimeStats } from '../models/models';
import { IListResponse } from '../../shared/models/IListResponse';
import { ImageResizeType } from '../settings/models/settings-models';
import { UnitPurpose } from '../experience/experiences';

@Injectable({
  providedIn: 'root'
})
export class ExperienceService {
  private http = inject(HttpClient);

  // campaigns
  // ---------

  // get campaigns
  getUnits(filters: Params): Observable<IListResponse<ExperienceUnit>> {
    const cleanedFilters = Object.keys(filters || {}).reduce((acc, key) => {
      const value = (filters as any)[key];
      if (value === null || value === undefined || value === 'null') { return acc; }
      (acc as any)[key] = value;
      return acc;
    }, {} as Params);

    return this.http.get<IListResponse<ExperienceUnit>>(`${config.adminBaseUrl}/api/experience/units`, { params: cleanedFilters });
  }

  // get campaigns + offers
  getAllUnits(filters: Params): Observable<IListResponse<ExperienceUnitListItem>> {
    const units = this.http.get<IListResponse<ExperienceUnitListItem>>(`${config.adminBaseUrl}/api/experience/all_units`, { params: filters });
    return units;
  }

  getSingleUnit(id: number): Observable<ExperienceUnit> {
    const unit = this.http.get<ExperienceUnit>(`${config.adminBaseUrl}/api/experience/units/${id}`).pipe(
      map(v => _.cloneDeep(v)));
    return unit;
  }

  public async changeStatus(id: number, request: StatusRequest): Promise<void> {
    await lastValueFrom(this.http.patch<void>(`${config.adminBaseUrl}/api/experience/units/${id}/change_status`, request));
  }

  public async changeOrder(request: ChangeOrderRequest): Promise<void> {
    await lastValueFrom(this.http.patch<void>(`${config.adminBaseUrl}/api/experience/units/change_order`, request));
  }

  public async addUnit(request: ExperienceUnit): Promise<number> {
    return lastValueFrom(this.http.post<number>(`${config.adminBaseUrl}/api/experience/units`, request));
  }

  public async duplicateUnit(unitId: number): Promise<void> {
    await lastValueFrom(this.http.post<number>(`${config.adminBaseUrl}/api/experience/units/${unitId}/duplicate`, {}));
  }

  public async saveUnitAsOffer(unitId: number): Promise<void> {
    await lastValueFrom(this.http.post<number>(`${config.adminBaseUrl}/api/experience/units/${unitId}/save_as_offer`, {}));
  }

  public async updateUnit(id: number, request: ExperienceUnit): Promise<void> {
    return lastValueFrom(this.http.patch<void>(`${config.adminBaseUrl}/api/experience/units/${id}`, request));
  }

  // remarketing
  // -----------

  getRemarketingUnitById(id: number): Observable<ExperienceRemarketing> {
    const unit = this.http.get<ExperienceRemarketing>(`${config.adminBaseUrl}/api/experience/units/${id}`).pipe(
      map(v => _.cloneDeep(v)));
    return unit;
  }

  public async addRemarketingUnit(request: ExperienceRemarketing): Promise<number> {
    return lastValueFrom(this.http.post<number>(`${config.adminBaseUrl}/api/experience/units`, request));
  }

  public async updateRemarketingUnit(id: number, request: ExperienceRemarketing): Promise<void> {
    return lastValueFrom(this.http.patch<void>(`${config.adminBaseUrl}/api/experience/units/${id}`, request));
  }

  // embedded groups
  // ---------------

  getUnitGroups(returnUsage: boolean = false): Observable<IListResponse<ExperienceUnitGroupListItem>> {
    return this.http.get<IListResponse<ExperienceUnitGroupListItem>>(`${config.adminBaseUrl}/api/experience/groups?returnUsage=${returnUsage}`);
  }

  addUnitGroup(request: ExperienceUnitGroup): Observable<number> {
    return this.http.post<number>(`${config.adminBaseUrl}/api/experience/groups`, request);
  }

  updateUnitGroup(request: ExperienceUnitGroup): Observable<void> {
    const { id, ...body } = request;
    return this.http.put<void>(`${config.adminBaseUrl}/api/experience/groups/${id}`, body);
  }

  uploadImage(request: ExperienceUnitImage): Observable<{ imgLink: string }> {
    request.resizeType ??= ImageResizeType.NoResize;
    request.isReturnFullUrl ??= false;
    const formData = new FormData();
    Object.keys(request).forEach(key => formData.append(key, request[key]));
    const res = this.http.post<{ imgLink: string }>(`${config.adminBaseUrl}/api/experience/uploadImage`, formData);
    return res;
  }

  deleteUnitGroup(groupId: number): Observable<void> {
    return this.http.delete<void>(`${config.adminBaseUrl}/api/experience/groups/${groupId}`);
  }

  // leads
  // -----

  getUnitsWithLeads(filters: Params): Observable<KeyValuePair[]> {
    // const fromDateStr = fromDate ? moment(fromDate).format('YYYY-MM-DD') : '';
    // const toDateStr = toDate ? moment(toDate).format('YYYY-MM-DD') : '';
    return this.http.get<KeyValuePair[]>(`${config.adminBaseUrl}/api/Experience/units-with-leads`, { params: filters });
  }

  getUnitLeads(filters: Params): Observable<{ data: UnitLead[]; count: number }> {
    // const params: { [param: string]: string } = {
    //   period,
    // };
    // if (unitId) { params.unit_id = String(unitId); }
    // if (pageSize) { params.page_size = String(pageSize); }
    // if (page) { params.page = String(page); }
    //
    return this.http.get<{ data: UnitLead[]; count: number }>(`${config.insightsBaseUrl}/api/unitleads`, { params: filters });
  }

  getUnitLeadsCount(filters: Params): Observable<number> {
    // let query = `?period=${period}`;
    // if (unitId) {
    //   query += `&unit_id=${unitId}`;
    // }
    return this.http.get<number>(`${config.insightsBaseUrl}/api/unitleads/count`, { params: filters });
  }

  getUnitLeadById(leadId: number): Observable<UnitLead> {
    return this.http.get<UnitLead>(`${config.insightsBaseUrl}/api/unitleads/single/?lead_id=${leadId}`);
  }

  // others
  // ------

  getUnitsStats(filters: Params): Observable<DataResponse<InsightsUnitStats>> {
    return this.http.get<DataResponse<InsightsUnitStats>>(`${config.insightsBaseUrl}/api/personalization/units/stats`, { params: filters });
  }

  getUnitsGraphData(filters: Params): Observable<DataResponse<InsightsUnitGraphItem[]>> {
    return this.http.get<DataResponse<InsightsUnitGraphItem[]>>(`${config.insightsBaseUrl}/api/personalization/units/graph`, { params: filters });
  }

  getAllSurveyQuestions(): Observable<SurveyQuestionForAudience[]> {
    const questions = this.http.get<SurveyQuestionForAudience[]>(`${config.adminBaseUrl}/api/experience/survey-questions`);
    return questions;
  }

  getUnitAllTimeStats(unitId: number): Observable<DataResponse<UnitAllTimeStats>> {
    return this.http.get<DataResponse<UnitAllTimeStats>>(`${config.insightsBaseUrl}/api/personalization/unit/alltimestats/?unit_id=${unitId}`);
  }

  getSurveyResult(unitId: number): Observable<DataResponse<SurveyQuestionResult[]>> {
    return this.http.get<DataResponse<SurveyQuestionResult[]>>(`${config.insightsBaseUrl}/api/personalization/survey/results/?unit_id=${unitId}`);
  }

  getUnitTargeting(unit: ExperienceTargets): ExperienceTargets {
    return {
      predefinedAudiences: unit.predefinedAudiences,
      segmentIds: unit.segmentIds,
      audienceFilter: unit.audienceFilter,
      urlFilter: unit.urlFilter,
      deviceFilter: unit.deviceFilter,
      maFilter: unit.maFilter,
    };
  }

  getDefaultVisibility(filterUnitsTypes: UnitPurpose): UnitVisibilityType {
    switch (filterUnitsTypes) {
      case 'engagment':
        return 'embedded';
      case 'promote':
        return 'slider';
      default:
        return 'light';
    }
  }
}
